<template>
  <BModal
    v-if="authorized"
    :id="id"
    :visible="show"
    scrollable
    no-close-on-backdrop
    :size="modalSize()"
    body-class="p-0 border-0 shadow-none"
    @hide="$emit('hide')"
  >
    <template #modal-header>
      <div class="d-flex w-100 single-page-element-position max-w-100">
        <div class="my-auto text-center pl-0">
          <h5 class="text-primary">Chat with Vendor</h5>
        </div>
        <div class="ml-auto text-center pr-0" style="margin-top: -3px">
          <BushelBtn
            v-if="allowFullScreenToggle"
            variant="secondary"
            flat
            btn-container-class="btn-xs modal-close mr-1"
            button-id="expandModal"
            :tooltip-message="showingFullScreen ? 'Compress to Regular Screen' : 'Expand to Full Screen'"
            tooltip-placement="left"
            style="margin-top: -5px"
            @click="showingFullScreen = !showingFullScreen"
          >
            <i :class="showingFullScreen ? 'fas fa-compress' : 'fas fa-expand'" />
          </BushelBtn>
          <button type="button" class="modal-close" aria-label="Close" :disabled="submitting ? true : false" @click="closeWindow()">
            <i
              :class="submitting ? 'fas fa-circle-notch fa-spin' : 'fa fa-times-thin'"
              :style="submitting ? 'font-size: 1.05em; margin-top: 5px;' : 'font-size: 1.5em; margin-top: 0px;'"
            />
          </button>
        </div>
      </div>
    </template>
    <div v-if="!dataReady" class="col-12 pt-5 mt-5">
      <div class="text-center">
        <ChatLoadingDots class="mt-5" />
      </div>
    </div>
    <div v-if="dataReady">
      <div v-if="!newChat" class="overflow-hidden">
        <ChatDialog
          v-if="activeChat"
          :key="'chat_storefront_' + activeChat.uuid"
          :active-chat="activeChat"
          :full-active-chat="activeChat"
          :loading="loading"
          :user="user"
          :data-ready="dataReady"
          :from-company="fromCompany"
          :from-company-id="fromCompanyId"
          :to-company-id="toCompanyId"
          :to-company-name="toCompanyName"
          :to-company-uuid="toCompanyUuid"
          :invoices-available="invoicesAvailable"
          :invoices-loaded="invoicesLoaded"
          :loading-invoices="loadingInvoices"
          @messages-updated="messagesUpdated"
        />
      </div>
      <div v-if="newChat">
        <div class="text-center p-4">
          <i class="far fa-comment-dots fa-5x text-light p-5 m-4" />
        </div>
        <NewChatMessage
          :value="newChatMessage"
          :error="submitted && !newChatMessage && newChatImages.length == 0 && newChatInvoices.length == 0 ? true : false"
          :submitted="submitted"
          :submitting="submitting"
          :loading="loading"
          :invoices-available="invoicesAvailable"
          :invoices-loaded="invoicesLoaded"
          :loading-invoices="loadingInvoices"
          :clear-dialog="refresh"
          :disabled="submitting || loading"
          @send-message="startNewChatAndMessage"
        />
      </div>
    </div>
    <template #modal-footer>
      <BushelBtn :disabled="submitting" variant="dark" ripple-color="dark" flat @click="closeWindow()"> Close </BushelBtn>
    </template>
  </BModal>
</template>

<script>
import ChatLoadingDots from "@/components/chat/ChatLoadingDots.vue";
import ChatDialog from "@/components/chat/ChatDialog.vue";
import NewChatMessage from "@/components/chat/NewChatMessage.vue";

export default {
  name: "StoreFrontChatWindow",
  components: { ChatLoadingDots, ChatDialog, NewChatMessage },
  props: {
    authorized: { type: Boolean, default: false },
    id: { type: String, default: "StoreFrontChatWindow" },
    show: { type: Boolean, required: true },
    allowFullScreenToggle: { type: Boolean, default: true },
    chat: { type: Object, default: null },
    user: { type: Object, required: true },
    fromCompany: { type: Object, required: true },
    toCompanyId: { type: Number, required: true },
    toCompanyUuid: { type: String, required: true },
    toCompanyName: { type: String, default: null },
    fromCompanyId: { type: Number, required: true },
    loading: { type: Boolean, default: false },
    refresh: { type: Boolean, default: false },
    invoicesAvailable: {
      type: Array,
      default() {
        return [];
      },
    },
    invoicesLoaded: { type: Boolean, default: false },
    loadingInvoices: { type: Boolean, default: false },
  },
  emits: ["hide", "startedNewChatAndMessage", "chatMessagesUpdated"],
  data() {
    return {
      showingFullScreen: false,
      submitting: false,
      submitted: false,
      dataReady: false,
      newChat: false,
      newChatMessage: null,
      newChatImages: [],
      newChatInvoices: [],
      activeChat: null,
    };
  },
  computed: {},
  watch: {
    chat(newVal, oldVal) {
      if (newVal != oldVal) {
        this.setChatService();
      }
    },
    refresh(newVal, oldVal) {
      if (newVal) {
        this.refreshChat();
      }
    },
  },
  created() {},
  mounted() {
    this.setChatService();
  },
  beforeUnmount() {},
  updated() {},
  methods: {
    modalSize() {
      if (this.showingFullScreen) {
        return "full"; // mh-sm-50 mh-md-70
      }
      return "lg"; // mh-sm-50 mh-md-70
    },
    closeWindow() {
      this.$emit("hide");
    },
    setChatService() {
      if (this.chat) {
        this.activeChat = this.chat;
        this.dataReady = true;
        this.newChat = false;
      } else {
        this.activeChat = null;
        this.dataReady = true;
        this.newChat = true;
      }
    },
    refreshChat() {
      this.setChatService();
      this.newChatMessage = null;
      this.newChatImages = [];
      this.newChatInvoices = [];
      this.submitted = false;
      this.submitting = false;
    },
    startNewChatAndMessage(incoming) {
      const { toCompanyId } = this;
      const currentCompanyId = this.fromCompanyId;
      this.newChatMessage = incoming.message;
      this.newChatImages = incoming.images;
      this.newChatInvoices = incoming.invoices;
      this.submitted = true;
      if (
        toCompanyId &&
        currentCompanyId &&
        (this.newChatMessage || (this.newChatImages && this.newChatImages.length > 0) || (this.newChatInvoices && this.newChatInvoices.length > 0))
      ) {
        this.submitting = true;
        this.$emit("startedNewChatAndMessage", {
          userId: this.user.id,
          currentCompanyId,
          toCompanyId,
          newMessage: this.newChatMessage,
          attachments: {
            images: this.newChatImages,
            invoices: this.newChatInvoices,
          },
        });
      }
    },
    messagesUpdated(val) {
      this.$emit("chatMessagesUpdated", val);
    },
  },
};
</script>

<style scoped></style>

<style lang="scss" scoped></style>
